import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    CircularProgress,
    Link,
    Pagination,
    Paper,
    Typography,
} from "@mui/material";
import debounce from "lodash/debounce";
import HomeService from "../../services/HomeService";
import { getContentLanguage } from "../../services/LangPrefs";
import Logger from "../../services/Logger";
import { trackEvent } from "../../services/GoogleAnalytics";
import CustomSearchField from "../components/CustomSearchField";
import { usePagesTranslation } from "../locales";
import StorybookReader from "./StorybookReader";
import { SURFACE } from "../../dark-theme/palette";
import { Link as RouterLink } from "react-router-dom";
import {
    buildReadStoryPath,
    getHomeStoryRowId,
    getLocalizedStoryFromHomeStoryRo,
    isHomeStorybookShelfRow,
    pickStoryCoverImageUrl,
} from "./storyRoUtils";

const PAGE_SIZE = 10;
const BOOK_WIDTH = 112;
const BOOK_HEIGHT = 168;
/** Warm sand: the shelf label keeps its wooden feel on the dark surface. */
const SHELF_LABEL_COLOR = "#d9c3a5";

function StorybookCover({ row, selected, onSelect, contentLanguage }) {
    const { title } = getLocalizedStoryFromHomeStoryRo(row, contentLanguage);
    const displayTitle = title || row?.templateTitle || "";
    const coverUrl = pickStoryCoverImageUrl(row);
    const [imageFailed, setImageFailed] = useState(false);
    const storyId = getHomeStoryRowId(row);
    const href = buildReadStoryPath(storyId);
    if (!coverUrl || !displayTitle || imageFailed) return null;

    const handleActivate = (event) => {
        if (
            href
            && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1)
        ) {
            return;
        }
        if (href) {
            event.preventDefault();
        }
        onSelect(row);
    };

    return (
        <Box
            component={href ? RouterLink : "button"}
            to={href || undefined}
            type={href ? undefined : "button"}
            onClick={handleActivate}
            aria-pressed={selected}
            sx={{
                width: BOOK_WIDTH,
                flexShrink: 0,
                p: 0,
                border: "none",
                cursor: "pointer",
                bgcolor: "transparent",
                textAlign: "left",
                borderRadius: "6px 10px 10px 6px",
                outline: selected ? "3px solid #3BA666" : "none",
                outlineOffset: 2,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                transform: selected ? "translateY(-4px) scale(1.02)" : "none",
                boxShadow: selected
                    ? "0 8px 20px rgba(59, 166, 102, 0.35)"
                    : "0 4px 12px rgba(0,0,0,0.12)",
                "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
                },
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    height: BOOK_HEIGHT,
                    borderRadius: "4px 8px 8px 4px",
                    overflow: "hidden",
                    bgcolor: "#5c4033",
                    border: "1px solid",
                    borderColor: selected ? "#3BA666" : "rgba(0,0,0,0.2)",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                    <Box
                        component="img"
                        src={coverUrl}
                        alt={displayTitle}
                        onError={() => setImageFailed(true)}
                        sx={{
                            width: "100%",
                            flex: 1,
                            minHeight: 0,
                            objectFit: "cover",
                        }}
                    />
                <Box
                    sx={{
                        px: 0.75,
                        py: 0.5,
                        bgcolor: "rgba(44, 62, 45, 0.92)",
                        borderTop: "1px solid rgba(255,255,255,0.12)",
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: "#fff",
                            fontWeight: 600,
                            lineHeight: 1.25,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            wordBreak: "break-word",
                        }}
                    >
                        {displayTitle}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default function HomeStorybookPage() {
    const { t } = usePagesTranslation();
    const contentLanguage = getContentLanguage();

    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [listLoading, setListLoading] = useState(true);
    const [selectedRow, setSelectedRow] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [titleKeyword, setTitleKeyword] = useState("");

    const applyTitleKeyword = useMemo(
        () =>
            debounce((value) => {
                setTitleKeyword(typeof value === "string" ? value.trim() : "");
                setPage(0);
            }, 300),
        [],
    );

    useEffect(() => () => applyTitleKeyword.cancel(), [applyTitleKeyword]);

    const handleSearchTitle = useCallback(
        (kw) => {
            applyTitleKeyword(kw);
        },
        [applyTitleKeyword],
    );

    const loadPage = useCallback(async (pageIndex, keyword) => {
        setListLoading(true);
        try {
            const result = await HomeService.getStoriesPage({
                page: pageIndex,
                pageSize: PAGE_SIZE,
                language: contentLanguage,
                keyword: keyword || undefined,
            });
            const content = Array.isArray(result.content) ? result.content : [];
            const visible = content.filter(isHomeStorybookShelfRow);
            setRows(visible);
            const kw = typeof keyword === "string" ? keyword.trim() : "";
            let tp = result.totalPages;
            if (kw && visible.length === 0) {
                tp = 0;
            }
            setTotalPages(typeof tp === "number" && tp >= 1 ? tp : visible.length > 0 ? 1 : 0);
        } catch (e) {
            Logger.error("HomeStorybookPage:", e);
            setRows([]);
            setTotalPages(0);
        }
        setListLoading(false);
    }, [contentLanguage]);

    useEffect(() => {
        loadPage(page, titleKeyword);
    }, [page, titleKeyword, loadPage]);

    useEffect(() => {
        trackEvent("entered", { name: "HomeStorybookPage", page_type: "STORYBOOK" });
    }, []);

    useEffect(() => {
        if (!rows.length) {
            setSelectedRow(null);
            return;
        }
        const sid = getHomeStoryRowId(selectedRow);
        const stillInList = sid && rows.some((r) => getHomeStoryRowId(r) === sid);
        if (!stillInList) {
            setSelectedRow(rows[0]);
        }
    }, [rows, selectedRow]);

    const handleSelect = useCallback(
        async (row) => {
            setSelectedRow(row);
            const storyId = getHomeStoryRowId(row);
            if (storyId) {
                trackEvent("storybook_story_select", { story_id: storyId });
            }

            const { text } = getLocalizedStoryFromHomeStoryRo(row, contentLanguage);
            if (String(text || "").trim().length > 80) return;

            if (!storyId) return;

            setDetailLoading(true);
            try {
                const detail = await HomeService.getStoryById(storyId);
                if (detail && detail?.code !== "NO_DATA") {
                    setSelectedRow(detail);
                }
            } catch (e) {
                Logger.error("HomeStorybookPage detail:", e);
            }
            setDetailLoading(false);
        },
        [contentLanguage]
    );

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2 }, width: "100%", boxSizing: "border-box" }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t("storybook.page-title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("storybook.page-subtitle")}
            </Typography>
            {rows.length > 0 ? (
                <Box
                    component="nav"
                    aria-label={t("stories.page-title", "Stories")}
                    sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1.25, rowGap: 0.5 }}
                >
                    {rows.map((row) => {
                        const id = getHomeStoryRowId(row);
                        const href = buildReadStoryPath(id);
                        if (!href) return null;
                        const { title } = getLocalizedStoryFromHomeStoryRo(row, contentLanguage);
                        const label = (title || row?.templateTitle || "").trim();
                        if (!label) return null;
                        return (
                            <Link
                                key={id}
                                component={RouterLink}
                                to={href}
                                sx={{
                                    fontSize: "0.875rem",
                                    color: "text.secondary",
                                    textDecoration: "underline",
                                }}
                            >
                                {label}
                            </Link>
                        );
                    })}
                </Box>
            ) : null}

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 2,
                    minHeight: { md: "calc(100vh - 12rem)" },
                }}
            >
                <Paper
                    variant="outlined"
                    sx={{
                        flex: { md: "0 0 280px" },
                        p: 1.5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        bgcolor: SURFACE.card,
                        /** Keeps the wooden-shelf hint now that the shelf is dark. */
                        backgroundImage:
                            "linear-gradient(180deg, rgba(146, 104, 74, 0.22) 0%, transparent 28px)",
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: SHELF_LABEL_COLOR }}>
                        {t("storybook.shelf-label")}
                    </Typography>
                    <CustomSearchField
                        callback={handleSearchTitle}
                        debounceMs={0}
                        placeholder={t("storybook.search-by-title")}
                        id="storybook-title-search"
                        sx={{ width: "100%" }}
                        aria-label={t("storybook.search-by-title")}
                    />
                    {listLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : rows.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            {titleKeyword.trim()
                                ? t("storybook.search-no-results")
                                : t("storybook.empty")}
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                                justifyContent: "center",
                                maxHeight: { xs: 280, md: "calc(100vh - 16rem)" },
                                overflowY: "auto",
                                /** Room for selected lift + green outline so the top edge is not clipped. */
                                pt: 3,
                                px: 1.5,
                                pb: 1.5,
                            }}
                        >
                            {rows.map((row) => {
                                const id = getHomeStoryRowId(row);
                                const selected =
                                    Boolean(selectedRow) &&
                                    getHomeStoryRowId(selectedRow) === id;
                                return (
                                    <StorybookCover
                                        key={id || row?.templateId}
                                        row={row}
                                        selected={selected}
                                        onSelect={handleSelect}
                                        contentLanguage={contentLanguage}
                                    />
                                );
                            })}
                        </Box>
                    )}
                    {totalPages > 1 && !listLoading && rows.length > 0 ? (
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Pagination
                                count={totalPages}
                                page={page + 1}
                                onChange={(_, v) => setPage(v - 1)}
                                color="primary"
                                size="small"
                            />
                        </Box>
                    ) : null}
                </Paper>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {selectedRow ? (
                        <StorybookReader
                            row={selectedRow}
                            contentLanguage={contentLanguage}
                            loadingDetail={detailLoading}
                            showCopyright={false}
                            showPermalink
                        />
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 240,
                                color: "text.secondary",
                            }}
                        >
                            <Typography variant="body1">{t("storybook.select-prompt")}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
